import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useFormProtection } from '../hooks/useFormProtection';
import HoneypotField from './HoneypotField';
import RecaptchaNotice from './RecaptchaNotice';

const ComplianceFooter = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { honeypotProps, onInteraction, getProtectionPayload } = useFormProtection('waitlist_signup');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const protection = await getProtectionPayload();

      const response = await axios.post('/api/contact/short', {
        email: email,
        source: 'Compliance Footer',
        ...protection
      });

      if (response.data.status === 'success') {
        navigate('/success');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert(error.response?.data?.detail || 'There was an error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 sm:p-8 lg:p-12">
          <div className="text-center space-y-6 sm:space-y-8">
            <div className="flex justify-center mb-4 sm:mb-6">
              <Shield className="w-12 h-12 sm:w-16 sm:h-16 text-blue-600" />
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">
              Built to Enhance, Not Replace.
            </h2>
            
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Practice Dashboard seamlessly integrates with your existing Practice CS environment, providing the modern insights you need without disrupting your workflow.
            </p>
            
            <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-4 sm:gap-6 mb-6 sm:mb-8">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0" />
                <span className="text-sm sm:text-base text-gray-700">No system migration required</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0" />
                <span className="text-sm sm:text-base text-gray-700">Secure infrastructure</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0" />
                <span className="text-sm sm:text-base text-gray-700">Quick 1-2 week setup</span>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <HoneypotField {...honeypotProps} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={onInteraction}
                  placeholder="Enter your email..."
                  className="flex-1 px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 min-h-[44px]"
                  required
                  disabled={isSubmitting}
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors min-h-[44px] whitespace-nowrap"
                >
                  {isSubmitting ? 'Submitting...' : 'Interested!'}
                </button>
              </div>
              <RecaptchaNotice className="mt-3" />
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComplianceFooter;